import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FilterDropdown, {FilterDropdownRef} from './FilterDropdown';
import FilterLayout from './FilterLayout';
import FilterMultiSelect from './FilterMultiSelect';
import {FilterOption} from '../../../common/types/FilterOption';
import publicationsService from '../../services/publicationsService';
import {handleError} from '../../../common/utils/errorHandling';
import './filters.scss';

interface PublicationsFilterProps {
  publications?: string[];
  onFilterChange: (publications?: string[]) => void;
}

const PublicationsFilter: React.FC<PublicationsFilterProps> = ({
  publications = [],
  onFilterChange
}) => {
  const {t} = useTranslation();
  const [tempPublications, setTempPublications] = useState<string[]>(publications);
  const [publicationOptions, setPublicationOptions] = useState<FilterOption[]>([]);
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const hasActiveFilter = publications.length > 0;

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    handleError(async () => {
      const publicationsData = await publicationsService.getAllPublications();
      const options: FilterOption[] = publicationsData.map(publication => ({
        label: publication.name,
        value: publication.id
      }));
      setPublicationOptions(options);
    }, 'Error fetching publications');
  };

  const applyFilter = () => {
    const filteredPublications = tempPublications.length > 0 ? tempPublications : undefined;
    onFilterChange(filteredPublications);
  };

  const clearFilter = () => {
    setTempPublications([]);
    onFilterChange(undefined);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyFilter();
      filterDropdownRef.current?.close();
    } else if (event.key === 'Escape') {
      setTempPublications(publications);
      filterDropdownRef.current?.close();
    }
  };

  const handleApplyClick = () => {
    applyFilter();
    filterDropdownRef.current?.close();
  };

  const getDisplayLabel = () => {
    if (publications.length === 0) {
      return t('gallery.filters.publications');
    }
    return `${t('gallery.filters.publications')} | ${publications.length}`;
  };

  return (
    <FilterDropdown
      ref={filterDropdownRef}
      label={getDisplayLabel()}
      hasActiveFilter={hasActiveFilter}
      onClearFilter={clearFilter}
    >
      <FilterLayout
        title={t('gallery.filters.filterByPublications')}
        onClear={clearFilter}
        onApply={handleApplyClick}
      >
        <FilterMultiSelect
          value={tempPublications}
          options={publicationOptions}
          onChange={setTempPublications}
          onKeyDown={handleKeyPress}
          width={600}
        />
      </FilterLayout>
    </FilterDropdown>
  );
};

export default PublicationsFilter;

